'use client';
import { useToast } from '@/components/toast';

import { useFacebookPixelEvents } from '@/hooks/use-facebook-pixel-events';
import { useRouter } from '@/i18n/routing';
import { useAuth } from '@/lib/auth/use-auth';
import { dateUtils } from '@/utils/date-utils';
import { Calendar01Icon, Clock02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'next-intl';
import { registerForWebinar } from '../../../actions/webinar-action';

type WebinarCardProps = {
  title: string;
  webinarId: number;
  dateTime: string;
  locale: string;
  isWebinarRegistered: boolean;
  courseTitle: string;
  courseId: number;
  batchId?: number;
  batchName?: string;
  bachNumber?: number;
};

export default function WebinarCard({
  webinarId,
  title,
  dateTime,
  locale,
  isWebinarRegistered,
  courseTitle,
  courseId,
  batchId,
  batchName,
  bachNumber,
}: WebinarCardProps) {
  const { user } = useAuth();
  const router = useRouter();
  const { showToast } = useToast();

  const t = useTranslations();
  const { trackWebinarRegistration } = useFacebookPixelEvents();

  const { classDate, classTime } = dateUtils.formatter.forClassDateAndTime(
    dateTime,
    locale,
  );

  const handleWebinarRegister = async () => {
    if (!user) {
      router.push(`/login?redirect=/webinars/${webinarId}`);

      return;
    }

    try {
      await registerForWebinar(Number(webinarId));

      const customData = {
        webinar_id: webinarId,
        webinar_title: title,
        webinar_date: dateTime,
        course_id: courseId,
        course_title: courseTitle,
        ...(batchId && { batch_id: batchId }),
        ...(batchId && { batch_name: batchName }),
        ...(batchId && { batch_number: bachNumber }),
      };

      const userData = {
        user_id: user.id,
        user_email: user.email,
        user_phone: user.phone,
      };

      trackWebinarRegistration(userData, customData);

      router.push(`/webinars/${webinarId}/registration-success`);
    } catch (error) {
      showToast({
        title: t('webinar-details.webinarBenefitsCard.registration-failed'),
        variant: 'error',
      });
    }
  };

  return (
    <div className="flex flex-col gap-8 p-4 bg-white rounded-sm shadow-sm md:gap-12">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 mb-3">{title}</h1>
        <div className="flex items-center gap-4 text-gray-600">
          <div className="flex items-center gap-2">
            <HugeiconsIcon icon={Calendar01Icon} className="w-5 h-5" />
            <span>{classDate}</span>
          </div>
          <div className="flex items-center gap-2">
            <HugeiconsIcon icon={Clock02Icon} className="w-5 h-5" />
            <span>{classTime}</span>
          </div>
        </div>
      </div>
      {isWebinarRegistered ? (
        <button
          className="text-sm md:text-base bg-primary text-white px-8 py-3 rounded-md font-semibold hover:bg-primary-700 transition-colors"
          onClick={() => router.push(`/my-webinars/${webinarId}`)}
        >
          {t('webinar-details.webinarBenefitsCard.go-to-webinar')}
        </button>
      ) : (
        <button
          onClick={handleWebinarRegister}
          className="text-sm md:text-base bg-primary text-white px-8 py-3 rounded-md font-semibold hover:bg-primary-700 transition-colors"
        >
          {t('webinar-details.webinarBenefitsCard.register-now')}
        </button>
      )}
    </div>
  );
}
